import { supabase } from './client';
import { getOrganizationProfile } from './organization';
import type {
  Contact,
  Organization,
  ContactPhone,
  ContactEmail,
  PhoneType,
  EmailType,
  ContactOrganization,
  DuplicateCheckResult,
  CreateContactResult,
  CreateOrganizationResult
} from '../../types/contacts';

// Duplicate checking functions
export async function checkDuplicateOrganization(entityId: string, name: string, domain?: string) {
  try {
    const query = supabase
      .from('organizations')
      .select('id, name, domain')
      .eq('entity_id', entityId);
    
    if (name) {
      query.ilike('name', `%${name}%`);
    }
    
    if (domain) {
      query.or(`domain.ilike.%${domain}%`);
    }
    
    const { data, error } = await query;
    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error checking duplicate organizations:', error);
    throw error;
  }
}

export async function checkDuplicateContact(
  entityId: string,
  firstName: string,
  lastName: string,
  emails?: string[],
  phones?: string[]
) {
  try {
    // Check name matches
    let nameQuery = supabase
      .from('contacts')
      .select(`
        id,
        first_name,
        last_name
      `)
      .eq('entity_id', entityId);
    
    if (firstName && lastName) {
      nameQuery = nameQuery.or(`first_name.ilike.%${firstName}%,last_name.ilike.%${lastName}%`);
    }
    
    const { data: nameMatches, error: nameError } = await nameQuery;
    if (nameError) throw nameError;

    // If emails provided, check for email matches
    let emailMatches: any[] = [];
    if (emails?.length) {
      const { data: emailData, error: emailError } = await supabase
        .from('contact_emails')
        .select('contact_id, email')
        .in('email', emails);
      
      if (emailError) throw emailError;
      emailMatches = emailData || [];
    }

    // If phones provided, check for phone matches
    let phoneMatches: any[] = [];
    if (phones?.length) {
      const { data: phoneData, error: phoneError } = await supabase
        .from('contact_phones')
        .select('contact_id, phone')
        .in('phone', phones);
      
      if (phoneError) throw phoneError;
      phoneMatches = phoneData || [];
    }

    return {
      nameMatches: nameMatches || [],
      emailMatches: emailMatches || [],
      phoneMatches: phoneMatches || []
    };
  } catch (error) {
    console.error('Error checking duplicate contacts:', error);
    throw error;
  }
}

// Create functions with duplicate checking
export async function createOrganizationWithCheck(
  organization: Omit<Organization, 'id' | 'created_at' | 'updated_at'>
): Promise<CreateOrganizationResult> {
  try {
    console.log('Creating organization with data:', organization);
    
    // Check for duplicates
    const duplicates = await checkDuplicateOrganization(
      organization.entity_id,
      organization.name,
      organization.domain
    );

    if (duplicates.length > 0) {
      return {
        data: null,
        duplicates,
        error: null
      };
    }

    // If no duplicates, create organization
    const { data, error } = await supabase
      .from('organizations')
      .insert([organization])
      .select()
      .single();
    
    if (error) {
      console.error('Error creating organization:', error.message, error.details, error.hint);
      throw error;
    }
    
    console.log('Organization created successfully:', data);
    return { data, duplicates: [], error: null };
  } catch (error) {
    console.error('Error creating organization:', error);
    throw error;
  }
}

export async function createContactWithCheck(
  contact: Omit<Contact, 'id' | 'created_at' | 'updated_at'>,
  emails?: any[],
  phones?: any[]
): Promise<CreateContactResult> {
  try {
    // Remove organization_id if it's an empty string to avoid foreign key constraint error
    if (contact.organization_id === '') {
      contact.organization_id = null;
    }

    // Special handling for team contacts - log for debugging
    if (contact.type === 'team') {
      console.log('Creating team contact with data:', {
        type: contact.type,
        organization_id: contact.organization_id,
        organization_relationship: contact.organization_relationship,
        entity_id: contact.entity_id
      });
      
      // For team contacts, always set relationship to internal
      contact.organization_relationship = 'internal';
      
      // If no organization_id is provided, try to get the organization profile
      if (!contact.organization_id) {
        try {
          // Get the organization profile for this entity
          const profile = await getOrganizationProfile();
            
          if (profile) {
            // Use the organization_id from the profile, not the profile id itself
            if (profile.organization?.id) {
              contact.organization_id = profile.organization?.id;
              console.log('Set team contact organization_id from profile.organization:', profile.organization.id);
            } else {
              console.warn('Profile found but no organization_id available:', profile);
            }
          } else {
            // Try to get the organization profile by owner_id
            const { data: entityData } = await supabase
              .from('entities')
              .select('owner_id')
              .eq('id', contact.entity_id)
              .single();
              
            if (entityData?.owner_id) {
              const { data: profileByOwner } = await supabase
                .from('organization_profiles')
                .select('id, name')
                .eq('owner_id', entityData.owner_id)
                .maybeSingle();
                
              if (profileByOwner) {
                // Get the organization_id for this profile
                const { data: orgProfile } = await supabase
                  .from('organization_profiles')
                  .select('organization_id')
                  .eq('id', profileByOwner.id)
                  .single();
                
                if (orgProfile?.organization_id) {
                  contact.organization_id = orgProfile.organization_id;
                  console.log('Set team contact organization_id from profile by owner:', orgProfile.organization_id);
                } else {
                  console.warn('Profile found by owner but no organization_id available');
                }
              }
            }
          }
        } catch (err) {
          console.error('Failed to get organization profile for team contact:', err);
        }
      }
    }
    
    // Check for duplicates
    const duplicates = await checkDuplicateContact(
      contact.entity_id,
      contact.first_name,
      contact.last_name,
      emails?.map(e => e.email),
      phones?.map(p => p.phone)
    );

    const hasDuplicates = 
      duplicates.nameMatches.length > 0 ||
      duplicates.emailMatches.length > 0 ||
      duplicates.phoneMatches.length > 0;

    if (hasDuplicates) {
      return {
        data: null,
        duplicates,
        error: null
      };
    }

    // If no duplicates and organization is valid, create contact
    try {
      // Log the contact data before insertion
      console.log('Creating contact with data:', {
        ...contact,
        organization_id: contact.organization_id || 'null'
      });

      // Step 1: Create the contact
      const { data: contactData, error: contactError } = await supabase
        .from('contacts')
        .insert([contact])
        .select()
        .single();
      
      if (contactError) {
        console.error('Contact creation error:', contactError.message, contactError.details, contactError.hint);
        throw new Error(`Failed to create contact: ${contactError.message}`);
      }
      
      if (!contactData) {
        throw new Error('Failed to create contact: No data returned');
      }
      
      const contactId = contactData.id;
      
      // Step 2: Add emails if provided
      if (emails && emails.length > 0) {
        const emailRecords = emails.filter(email => email.email.trim()).map((email, index) => ({
          contact_id: contactId,
          email: email.email.trim(),
          type: email.type,
          is_primary: index === 0 // First one is primary
        }));
        
        if (emailRecords.length > 0) {
          const { error: emailError } = await supabase
            .from('contact_emails')
            .insert(emailRecords);
            
          if (emailError) {
            console.error('Email creation error:', emailError);
            throw new Error(`Failed to create contact emails: ${emailError.message}`);
          }
        }
      }
      
      // Step 3: Add phones if provided
      if (phones && phones.length > 0) {
        const phoneRecords = phones.filter(phone => phone.phone.trim()).map((phone, index) => ({
          contact_id: contactId,
          phone: phone.phone.trim(),
          type: phone.type,
          is_primary: index === 0 // First one is primary
        }));
        
        if (phoneRecords.length > 0) {
          const { error: phoneError } = await supabase
            .from('contact_phones')
            .insert(phoneRecords);
            
          if (phoneError) {
            console.error('Phone creation error:', phoneError);
            throw new Error(`Failed to create contact phones: ${phoneError.message}`);
          }
        }
      }
      
      return { 
        data: contactData, 
        duplicates: { nameMatches: [], emailMatches: [], phoneMatches: [] }, 
        error: null 
      };
    }
    catch (error) {
      if (error instanceof Error && error.message.includes('foreign key constraint') && 
          error.message.includes('contacts_organization_id_fkey')) {
        
        console.error('Foreign key constraint error:', error.message);
        console.log('Attempted to use organization_id:', contact.organization_id);
        
        // For team contacts, try to create without organization_id
        if (contact.type === 'team') {
          console.log('Retrying team contact creation without organization_id');

          // Try to get the correct organization_id from the profile
          let correctOrgId = null;
          try {
            const profile = await getOrganizationProfile();
            if (profile?.organization?.id) {
              correctOrgId = profile.organization.id;
              console.log('Found correct organization_id from profile:', correctOrgId);
            } 
          } catch (profileErr) {
            console.error('Failed to get correct organization_id:', profileErr);
          }
          
          try {
            const { data: contactData, error: retryError } = await supabase
              .from('contacts')
              .insert([{
                ...contact,
                organization_id: correctOrgId, // Use the correct organization_id or null
                organization_relationship: 'internal' // Force internal relationship
              }])
              .select()
              .single();
            
            if (retryError) {
              console.error('Retry contact creation error:', retryError);
              throw retryError;
            }
            
            if (!contactData) {
              throw new Error('Failed to create contact on retry: No data returned');
            }
            
            // Process emails and phones as before
            // ... rest of the code for processing emails and phones ...
            
            return {
              data: contactData,
              duplicates: { nameMatches: [], emailMatches: [], phoneMatches: [] },
              error: null
            };
          } catch (retryError) {
            console.error('Error in retry attempt:', retryError);
            throw retryError;
          }
        }
      }
      console.error('Error creating contact:', error);
      throw error;
    }
  } catch (error) {
    console.error('Error in createContactWithCheck:', error);
    throw error;
  }
}

// Get a single contact by ID
export async function getContact(contactId: string): Promise<Contact | null> {
  try {
    const { data, error } = await supabase
      .from('contacts')
      .select(`
        *,
        phones:contact_phones(id, phone, type, is_primary),
        emails:contact_emails(id, email, type, is_primary),
        organization:organizations!contacts_organization_id_fkey(id, name)
      `)
      .eq('id', contactId)
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error getting contact:', error);
    throw error;
  }
}

// Update a contact
export async function updateContact(
  contactId: string,
  contact: Partial<Omit<Contact, 'id' | 'created_at' | 'updated_at'>>,
  emails?: any[],
  phones?: any[]
): Promise<Contact | null> {
  try {
    console.log('=== UPDATE CONTACT FUNCTION START ===');
    console.log('Contact ID:', contactId);
    console.log('Update data:', contact);
    console.log('Emails count:', emails?.length || 0);
    console.log('Phones count:', phones?.length || 0);

    // Remove organization_id if it's an empty string
    if (contact.organization_id === '') {
      contact.organization_id = null;
    }

    // Special handling for team contacts
    if (contact.type === 'team') {
      // For team contacts, always set relationship to internal
      contact.organization_relationship = 'internal';
      
      // If no organization_id is provided, try to get the organization profile
      if (!contact.organization_id) {
        try {
          const profile = await getOrganizationProfile();
          if (profile?.organization?.id) {
            // Use the organization_id from the profile instead
            contact.organization_id = profile.organization.id;
            console.log('Set team contact org ID from profile:', contact.organization_id);
          }
        } catch (profileErr) {
          console.error('Profile lookup failed:', profileErr);
        }
      }
    }

    // Step 1: Update the contact
    console.log('=== STEP 1: UPDATING CONTACT RECORD ===');
    const { data: contactData, error: contactError } = await supabase
      .from('contacts')
      .update(contact)
      .eq('id', contactId)
      .select()
      .single();
    
    if (contactError) {
      console.error('Contact update error:', contactError);
      throw contactError;
    }
    if (!contactData) {
      console.error('No contact data returned');
      throw new Error('Failed to update contact');
    }
    
    console.log('Contact record updated successfully');
    
    // Step 2: Handle emails
    if (emails && emails.length > 0) {
      console.log('=== STEP 2: PROCESSING EMAILS ===');
      // First, get existing emails to determine which to update, delete, or insert
      const { data: existingEmails, error: emailsError } = await supabase
        .from('contact_emails')
        .select('id')
        .eq('contact_id', contactId);
      
      if (emailsError) {
        console.error('Error fetching existing emails:', emailsError);
        throw emailsError;
      }
      
      const existingEmailIds = new Set((existingEmails || []).map(e => e.id));
      const emailsToUpdate = emails.filter(e => e.id && existingEmailIds.has(e.id));
      const emailsToInsert = emails.filter(e => !e.id && e.email.trim());
      const emailIdsToKeep = new Set(emailsToUpdate.map(e => e.id));
      const emailIdsToDelete = [...existingEmailIds].filter(id => !emailIdsToKeep.has(id));
      
      console.log('Email processing:', {
        existing: existingEmailIds.size,
        toUpdate: emailsToUpdate.length,
        toInsert: emailsToInsert.length,
        toDelete: emailIdsToDelete.length
      });
      
      // Delete emails that are no longer in the list
      if (emailIdsToDelete.length > 0) {
        console.log('Deleting emails:', emailIdsToDelete);
        const { error: deleteError } = await supabase
          .from('contact_emails')
          .delete()
          .in('id', emailIdsToDelete);
        
        if (deleteError) {
          console.error('Error deleting emails:', deleteError);
          throw deleteError;
        }
      }
      
      // Update existing emails
      for (const email of emailsToUpdate) {
        if (!email.email.trim()) continue;
        
        console.log('Updating email:', email.id);
        const { error: updateError } = await supabase
          .from('contact_emails')
          .update({
            email: email.email.trim(),
            type: email.type,
            is_primary: email.is_primary
          })
          .eq('id', email.id);
        
        if (updateError) {
          console.error('Error updating email:', updateError);
          throw updateError;
        }
      }
      
      // Insert new emails
      if (emailsToInsert.length > 0) {
        console.log('Inserting new emails:', emailsToInsert.length);
        const emailRecords = emailsToInsert.map(email => ({
          contact_id: contactId,
          email: email.email.trim(),
          type: email.type,
          is_primary: email.is_primary || false
        }));
        
        const { error: insertError } = await supabase
          .from('contact_emails')
          .insert(emailRecords);
        
        if (insertError) {
          console.error('Error inserting emails:', insertError);
          throw insertError;
        }
      }
      console.log('Email processing completed');
    }
    
    // Step 3: Handle phones
    if (phones && phones.length > 0) {
      console.log('=== STEP 3: PROCESSING PHONES ===');
      // First, get existing phones to determine which to update, delete, or insert
      const { data: existingPhones, error: phonesError } = await supabase
        .from('contact_phones')
        .select('id')
        .eq('contact_id', contactId);
      
      if (phonesError) {
        console.error('Error fetching existing phones:', phonesError);
        throw phonesError;
      }
      
      const existingPhoneIds = new Set((existingPhones || []).map(p => p.id));
      const phonesToUpdate = phones.filter(p => p.id && existingPhoneIds.has(p.id));
      const phonesToInsert = phones.filter(p => !p.id && p.phone.trim());
      const phoneIdsToKeep = new Set(phonesToUpdate.map(p => p.id));
      const phoneIdsToDelete = [...existingPhoneIds].filter(id => !phoneIdsToKeep.has(id));
      
      console.log('Phone processing:', {
        existing: existingPhoneIds.size,
        toUpdate: phonesToUpdate.length,
        toInsert: phonesToInsert.length,
        toDelete: phoneIdsToDelete.length
      });
      
      // Delete phones that are no longer in the list
      if (phoneIdsToDelete.length > 0) {
        console.log('Deleting phones:', phoneIdsToDelete);
        const { error: deleteError } = await supabase
          .from('contact_phones')
          .delete()
          .in('id', phoneIdsToDelete);
        
        if (deleteError) {
          console.error('Error deleting phones:', deleteError);
          throw deleteError;
        }
      }
      
      // Update existing phones
      for (const phone of phonesToUpdate) {
        if (!phone.phone.trim()) continue;
        
        console.log('Updating phone:', phone.id);
        const { error: updateError } = await supabase
          .from('contact_phones')
          .update({
            phone: phone.phone.trim(),
            type: phone.type,
            is_primary: phone.is_primary
          })
          .eq('id', phone.id);
        
        if (updateError) {
          console.error('Error updating phone:', updateError);
          throw updateError;
        }
      }
      
      // Insert new phones
      if (phonesToInsert.length > 0) {
        console.log('Inserting new phones:', phonesToInsert.length);
        const phoneRecords = phonesToInsert.map(phone => ({
          contact_id: contactId,
          phone: phone.phone.trim(),
          type: phone.type,
          is_primary: phone.is_primary || false
        }));
        
        const { error: insertError } = await supabase
          .from('contact_phones')
          .insert(phoneRecords);
        
        if (insertError) {
          console.error('Error inserting phones:', insertError);
          throw insertError;
        }
      }
      console.log('Phone processing completed');
    }
    
    console.log('=== UPDATE CONTACT COMPLETED SUCCESSFULLY ===');
    return contactData;
  } catch (error) {
    console.error('=== UPDATE CONTACT FUNCTION ERROR ===', error);
    throw error;
  }
}

// Update an organization record directly
export async function updateOrganizationRecord(
  organizationId: string,
  entityId: string,
  updates: Partial<Pick<Organization, 'name' | 'type' | 'domain'>>
): Promise<Organization> {
  try {
    const { data, error } = await supabase
      .from('organizations')
      .update(updates)
      .eq('id', organizationId)
      .eq('entity_id', entityId)
      .select()
      .single();
    
    if (error) throw error;
    if (!data) throw new Error('No organization data returned');
    
    return data;
  } catch (error) {
    console.error('Error updating organization record:', error);
    throw error;
  }
}

// Delete a contact
export async function deleteContact(contactId: string): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('contacts')
      .delete()
      .eq('id', contactId);
    
    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error deleting contact:', error);
    throw error;
  }
}

// Delete an organization
export async function deleteOrganization(organizationId: string): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('organizations')
      .delete()
      .eq('id', organizationId);
    
    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error deleting organization:', error);
    throw error;
  }
}

// CRUD operations for contacts
export async function getContacts(entityId: string) {
  try {
    if (!entityId) {
      console.log('getContacts: Entity ID is missing');
      return [];
    }
    console.log('getContacts: Loading contacts for entity', entityId);
    
    try {
      // First try with the explicit foreign key relationship
      const { data, error } = await supabase
        .from('contacts')
        .select(`
          *,
          phones:contact_phones(id, phone, type),
          emails:contact_emails(id, email, type),
          organization:organizations!contacts_organization_id_fkey(id, name)
        `)
        .eq('entity_id', entityId)
        .order('created_at', { ascending: false })
        .order('last_name', { ascending: true });

      if (error) {
        // If the first query fails, try without the organization relationship
        console.warn('getContacts: Error with organization relationship, trying without:', error);
        const { data: basicData, error: basicError } = await supabase
          .from('contacts')
          .select(`
            *,
            phones:contact_phones(id, phone, type),
            emails:contact_emails(id, email, type)
          `)
          .eq('entity_id', entityId)
          .order('created_at', { ascending: false })
          .order('last_name', { ascending: true });
          
        if (basicError) {
          console.error('getContacts: Error fetching basic contacts:', basicError);
          return []; // Return empty array instead of throwing to prevent UI breakage
        }
        
        console.log('getContacts: Successfully loaded basic contacts:', basicData?.length || 0);
        return basicData || [];
      }
      
      console.log('getContacts: Successfully loaded contacts with organizations:', data?.length || 0);
      return data || [];
    } catch (queryError) {
      console.error('getContacts: Query error:', queryError);
      return []; // Return empty array instead of throwing to prevent UI breakage
    }
  } catch (error) {
    console.error('Error getting contacts:', error);
    // Return empty array instead of throwing to prevent UI from breaking
    return [];
  }
}

export async function getOrganizations(entityId: string) {
  try {
    if (!entityId) {
      console.error('getOrganizations: Entity ID is required');
      return [];
    }

    console.log('Getting organizations for entity:', entityId);
    const { data, error } = await supabase
      .from('organizations')
      .select('*')
      .eq('entity_id', entityId)
      .order('name', { ascending: true });

    if (error) {
      console.error('Error fetching organizations:', error);
      return []; // Return empty array instead of throwing to prevent UI breakage
    }
    
    console.log('Organizations fetched:', data?.length || 0);
    return data || [];
  } catch (error) {
    console.error('getOrganizations: Failed to get organizations:', error);
    return [];
  }
}

// Get the current organization profile
export async function getCurrentOrganization(entityId: string) {
  try {
    if (!entityId) return null;
    
    // First try to get the organization profile
    const { data: profile } = await supabase
      .from('organizations')
      .select('id, name')
      .eq('entity_id', entityId)
      .maybeSingle();
    
    if (profile) {
      return {
        id: profile.id,
        name: profile.name
      };
    }
    
    return null;
  } catch (error) {
    console.error('Failed to get current organization:', error);
    return null;
  }
}

export async function getPhoneTypes() {
  return [
    { id: 'work', name: 'Work' },
    { id: 'mobile', name: 'Mobile' },
    { id: 'home', name: 'Home' },
    { id: 'other', name: 'Other' }
  ];
}

export async function getEmailTypes() {
  return [
    { id: 'work', name: 'Work' },
    { id: 'personal', name: 'Personal' },
    { id: 'other', name: 'Other' }
  ];
}

// Helper function to verify organization exists (referenced in updateContact but not defined)
async function verifyOrganizationExists(entityId: string, organizationId: string): Promise<boolean> {
  try {
    const { data, error } = await supabase
      .from('organizations')
      .select('id')
      .eq('id', organizationId)
      .eq('entity_id', entityId)
      .single();
    
    if (error) return false;
    return !!data;
  } catch (error) {
    console.error('Error verifying organization exists:', error);
    return false;
  }
}